/**
 * ModulePreview
 *
 * Teacher preview page for module content.
 * Reuses existing ModuleContentLoader and ContentSlider for learning content.
 * Shows quiz questions with answer toggle.
 */

import React, { useEffect, useState, useCallback, Suspense } from "react";
import {
  Container,
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Paper,
  Alert,
  Chip,
  Switch,
  Skeleton,
  Divider,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, Link as RouterLink } from "react-router-dom";

// Icons
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import QuizIcon from "@mui/icons-material/Quiz";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";

// Services
import {
  previewService,
  PreviewModuleContent,
  PreviewQuestion,
  getProgramDisplayName,
} from "../../../services/previewService";
import ModuleContentLoader, {
  LoadedSection,
} from "../../../services/ModuleContentLoader";

// Context
import { usePreview } from "../../../context/PreviewContext";

// Components - Reuse existing ContentSlider
import ContentSlider from "../../student/components/ContentSlider";

// ============================================================================
// Styled Components
// ============================================================================

const PreviewBanner = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: theme.palette.info.light,
  borderLeft: `4px solid ${theme.palette.info.main}`,
}));

const TabPanel = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2),
}));

// Student-like layout components
const ContentContainer = styled(Paper)(({ theme }) => ({
  display: "flex",
  borderRadius: "8px",
  overflow: "hidden",
  height: "calc(100vh - 320px)", // Taller viewport
  minHeight: "600px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    height: "auto",
    minHeight: "500px",
  },
}));

const SectionsSidebar = styled(Box)(({ theme }) => ({
  width: "280px",
  minWidth: "280px",
  backgroundColor: "#F8F9FA",
  borderRight: "1px solid #E9ECEF",
  padding: theme.spacing(2),
  overflowY: "auto",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    minWidth: "100%",
    borderRight: "none",
    borderBottom: "1px solid #E9ECEF",
    maxHeight: "200px",
  },
}));

const ContentMain = styled(Box)(({ theme }) => ({
  flex: 1,
  position: "relative",
  backgroundColor: "#FFFFFF",
  overflow: "hidden",
}));

const ContentArea = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
}));

const SectionNavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "10px 12px",
  margin: "4px 0",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  transition: "all 0.2s ease",
  backgroundColor: isActive ? "#E3F2FD" : "transparent",
  color: isActive ? "#1976D2" : "#495057",
  border: isActive ? "1px solid #2196F3" : "1px solid transparent",
  "&:hover": {
    backgroundColor: isActive ? "#E3F2FD" : "#F5F5F5",
  },
}));

const SectionNumber = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  width: "28px",
  height: "28px",
  minWidth: "28px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: 600,
  backgroundColor: isActive ? "#2196F3" : "#E9ECEF",
  color: isActive ? "white" : "#6C757D",
}));

// ============================================================================
// Types
// ============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ============================================================================
// Helper Components
// ============================================================================

const CustomTabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
}) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <TabPanel>{children}</TabPanel>}
    </div>
  );
};

// Helper to detect if asset is audio
const isAudioAsset = (url: string): boolean => {
  if (!url) return false;
  const audioExtensions = [".mp3", ".wav", ".ogg", ".m4a", ".aac", ".webm"];
  const lowercaseUrl = url.toLowerCase();
  return audioExtensions.some((ext) => lowercaseUrl.includes(ext));
};

// ============================================================================
// Main Component
// ============================================================================

const ModulePreview: React.FC = () => {
  const { program, weekNumber, moduleType } = useParams<{
    program: string;
    weekNumber: string;
    moduleType: string;
  }>();

  const { showAnswers, setShowAnswers } = usePreview();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Backend data (quiz questions)
  const [moduleData, setModuleData] = useState<PreviewModuleContent | null>(
    null,
  );

  // Frontend data (learning content)
  const [moduleConfig, setModuleConfig] = useState<any>(null);
  const [loadedSections, setLoadedSections] = useState<LoadedSection[]>([]);
  const [activeSectionIndex, setActiveSectionIndex] = useState<number | null>(
    null,
  );

  const weekNum = parseInt(weekNumber || "1", 10);

  // Loading states for each tab
  const [learningLoading, setLearningLoading] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [learningLoaded, setLearningLoaded] = useState(false);
  const [quizLoaded, setQuizLoaded] = useState(false);

  // Image viewer state
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Get cache functions from context
  const {
    getCachedModule,
    setCachedQuizData,
    setCachedLearningContent,
    getModuleKey,
  } = usePreview();

  // Generate module cache key
  const moduleKey =
    program && weekNum && moduleType
      ? getModuleKey(program, weekNum, moduleType)
      : null;

  // Track previous moduleKey to detect navigation (use ref to avoid re-renders)
  const prevModuleKeyRef = React.useRef<string | null>(null);
  const isInitialMount = React.useRef(true);

  // ============================================================================
  // Handle module navigation - reset states then check cache
  // This effect MUST run before the lazy load effects
  // ============================================================================

  useEffect(() => {
    // On initial mount, just set the ref and continue
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevModuleKeyRef.current = moduleKey;

      // Check cache on initial mount
      if (moduleKey) {
        const cached = getCachedModule(moduleKey);
        if (cached) {
          console.log("[Preview] Initial mount - found cache for:", moduleKey);
          if (cached.quizData) {
            setModuleData(cached.quizData);
            setQuizLoaded(true);
          }
          if (cached.learningConfig && cached.loadedSections.length > 0) {
            setModuleConfig(cached.learningConfig);
            setLoadedSections(cached.loadedSections);
            setLearningLoaded(true);
            setActiveSectionIndex(0);
          }
        }
      }
      return;
    }

    // Skip if moduleKey hasn't actually changed
    if (moduleKey === prevModuleKeyRef.current) return;

    console.log(
      "[Preview] Module NAVIGATION from",
      prevModuleKeyRef.current,
      "to",
      moduleKey,
    );
    prevModuleKeyRef.current = moduleKey;

    // If no moduleKey, just reset and return
    if (!moduleKey) {
      setModuleData(null);
      setModuleConfig(null);
      setLoadedSections([]);
      setActiveSectionIndex(null);
      setLearningLoaded(false);
      setQuizLoaded(false);
      setLearningLoading(false);
      setQuizLoading(false);
      setError(null);
      return;
    }

    // Check cache for the NEW module
    const cached = getCachedModule(moduleKey);

    if (cached) {
      console.log("[Preview] Found cache for:", moduleKey);

      // Restore quiz data if cached
      if (cached.quizData) {
        setModuleData(cached.quizData);
        setQuizLoaded(true);
      } else {
        setModuleData(null);
        setQuizLoaded(false);
      }

      // Restore learning content if cached
      if (cached.learningConfig && cached.loadedSections.length > 0) {
        setModuleConfig(cached.learningConfig);
        setLoadedSections(cached.loadedSections);
        setLearningLoaded(true);
        setActiveSectionIndex(0);
      } else {
        setModuleConfig(null);
        setLoadedSections([]);
        setLearningLoaded(false);
        setActiveSectionIndex(null);
      }

      setLearningLoading(false);
      setQuizLoading(false);
      setError(null);
    } else {
      console.log(
        "[Preview] No cache for:",
        moduleKey,
        "- resetting for fresh load",
      );

      // Reset all states for fresh load
      setModuleData(null);
      setModuleConfig(null);
      setLoadedSections([]);
      setActiveSectionIndex(null);
      setLearningLoaded(false);
      setQuizLoaded(false);
      setLearningLoading(false);
      setQuizLoading(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey]);

  // ============================================================================
  // Lazy Load: Learning Content (Tab 0)
  // Only triggers when tab is active AND content not yet loaded
  // ============================================================================

  useEffect(() => {
    // Don't load if we don't have required params
    if (!program || !weekNumber || !moduleType || !moduleKey) return;

    // Don't load if not on learning tab
    if (activeTab !== 0) return;

    // Don't load if already loaded or loading
    if (learningLoaded || learningLoading) return;

    // Don't load if moduleKey doesn't match (navigation in progress)
    if (moduleKey !== prevModuleKeyRef.current) return;

    const loadLearningContent = async () => {
      try {
        console.log("[Preview] Loading learning content for:", moduleKey);
        setLearningLoading(true);

        const config = await ModuleContentLoader.loadModuleConfig(
          program,
          `week${weekNum}`,
          moduleType,
        );

        // Double-check we're still on the same module
        if (moduleKey !== prevModuleKeyRef.current) {
          console.log("[Preview] Module changed during load, aborting");
          return;
        }

        setModuleConfig(config);

        if (config?.sections) {
          const sections = await ModuleContentLoader.loadSectionComponents(
            config.sections,
          );

          // Double-check again
          if (moduleKey !== prevModuleKeyRef.current) {
            console.log("[Preview] Module changed during load, aborting");
            return;
          }

          setLoadedSections(sections);
          setCachedLearningContent(moduleKey, config, sections);

          if (sections.length > 0) {
            setActiveSectionIndex(0);
          }
        }

        setLearningLoaded(true);
      } catch (err) {
        console.warn("No frontend config found for module:", err);
        setLearningLoaded(true);
      } finally {
        setLearningLoading(false);
      }
    };

    loadLearningContent();
  }, [
    program,
    weekNumber,
    weekNum,
    moduleType,
    moduleKey,
    activeTab,
    learningLoaded,
    learningLoading,
    setCachedLearningContent,
  ]);

  // ============================================================================
  // Lazy Load: Quiz Data (Tab 1)
  // Only triggers when tab is active AND quiz not yet loaded
  // ============================================================================

  useEffect(() => {
    // Don't load if we don't have required params
    if (!program || !weekNumber || !moduleType || !moduleKey) return;

    // Don't load if not on quiz tab
    if (activeTab !== 1) return;

    // Don't load if already loaded or loading
    if (quizLoaded || quizLoading) return;

    // Don't load if moduleKey doesn't match (navigation in progress)
    if (moduleKey !== prevModuleKeyRef.current) return;

    const loadQuizData = async () => {
      try {
        console.log("[Preview] Loading quiz data for:", moduleKey);
        setQuizLoading(true);
        setError(null);

        const backendData = await previewService.getModuleContent(
          program,
          weekNum,
          moduleType,
        );

        // Double-check we're still on the same module
        if (moduleKey !== prevModuleKeyRef.current) {
          console.log("[Preview] Module changed during load, aborting");
          return;
        }

        setModuleData(backendData);
        setCachedQuizData(moduleKey, backendData);
        setQuizLoaded(true);
      } catch (err: any) {
        console.error("Error loading quiz data:", err);
        setError(
          err.response?.status === 403
            ? "You don't have permission to preview this content."
            : "Failed to load quiz content. Please try again.",
        );
      } finally {
        setQuizLoading(false);
      }
    };

    loadQuizData();
  }, [
    program,
    weekNumber,
    weekNum,
    moduleType,
    moduleKey,
    activeTab,
    quizLoaded,
    quizLoading,
    setCachedQuizData,
  ]);

  // ============================================================================
  // Initial page load - just validate params
  // ============================================================================

  useEffect(() => {
    if (!program || !weekNumber || !moduleType) {
      setError("Invalid module parameters.");
    }
  }, [program, weekNumber, moduleType]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Don't reset section - keep selection when switching tabs
  };

  // Dummy handlers for ContentSlider (preview mode - no actual tracking)
  const handleSectionComplete = useCallback(() => {
    console.log("[Preview] Section viewed (no tracking)");
  }, []);

  // ============================================================================
  // Render Helpers - Quiz Questions (Redesigned UX)
  // ============================================================================

  const renderQuestionOptions = (question: PreviewQuestion) => {
    if (!question.options) return null;

    return (
      <Box sx={{ mt: 2 }}>
        {question.options.map((option, idx) => {
          const isCorrect = question.correctAnswer === idx;
          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                mb: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor:
                  showAnswers && isCorrect ? "success.main" : "grey.200",
                backgroundColor:
                  showAnswers && isCorrect ? "success.50" : "grey.50",
                transition: "all 0.2s ease",
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor:
                    showAnswers && isCorrect ? "success.main" : "grey.200",
                  color: showAnswers && isCorrect ? "white" : "grey.600",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                {showAnswers && isCorrect ? (
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                ) : (
                  String.fromCharCode(65 + idx)
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: showAnswers && isCorrect ? 600 : 400,
                  color:
                    showAnswers && isCorrect ? "success.dark" : "text.primary",
                }}
              >
                {option}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Render sub-questions (Type 1, 6, 10) - Redesigned
  const renderSubQuestions = (question: PreviewQuestion) => {
    if (!question.questions || question.questions.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        {question.questions.map((subQ: any, idx: number) => {
          const hasAnswer =
            (subQ.correctAnswer !== undefined &&
              subQ.correctAnswer !== null &&
              subQ.correctAnswer !== -1) ||
            subQ.correctAnswerText ||
            subQ.correctOption;

          return (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ mb: 1.5, color: "text.primary" }}
              >
                {subQ.question}
              </Typography>

              {/* Options for MCQ sub-questions */}
              {subQ.options && subQ.options.length > 0 && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
                >
                  {subQ.options.map((opt: string, optIdx: number) => {
                    const isCorrect =
                      subQ.correctAnswer === optIdx ||
                      subQ.correctAnswerText === opt ||
                      subQ.correctOption === opt;

                    return (
                      <Box
                        key={optIdx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          borderRadius: 1.5,
                          backgroundColor:
                            showAnswers && isCorrect ? "success.50" : "white",
                          border: "1px solid",
                          borderColor:
                            showAnswers && isCorrect
                              ? "success.main"
                              : "grey.200",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor:
                              showAnswers && isCorrect
                                ? "success.main"
                                : "grey.100",
                            color:
                              showAnswers && isCorrect ? "white" : "grey.500",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {showAnswers && isCorrect
                            ? "✓"
                            : String.fromCharCode(65 + optIdx)}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: showAnswers && isCorrect ? 500 : 400,
                            color:
                              showAnswers && isCorrect
                                ? "success.dark"
                                : "text.primary",
                          }}
                        >
                          {opt}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Show "No answer data" warning */}
              {subQ.options &&
                subQ.options.length > 0 &&
                !hasAnswer &&
                showAnswers && (
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontStyle="italic"
                    >
                      Answer data not available
                    </Typography>
                  </Box>
                )}

              {/* Text answer for fill-in-blank */}
              {subQ.answer && !subQ.options && (
                <Box sx={{ mt: 1 }}>
                  {showAnswers ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        backgroundColor: "success.50",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "success.200",
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ fontSize: 18, color: "success.main" }}
                      />
                      <Typography
                        variant="body2"
                        color="success.dark"
                        fontWeight={500}
                      >
                        {subQ.answer}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      fontStyle="italic"
                    >
                      [Answer hidden]
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
    );
  };

  // Render Type 3: Checkbox/Multi-select - Redesigned
  const renderCheckboxOptions = (question: PreviewQuestion) => {
    if (!question.options || question.options.length === 0) return null;

    const correctAnswers = Array.isArray(question.correctAnswer)
      ? question.correctAnswer
      : question.correctAnswer !== undefined
        ? [question.correctAnswer]
        : [];

    return (
      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {question.options.map((option, idx) => {
          const isCorrect =
            correctAnswers.includes(idx) || correctAnswers.includes(option);
          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                backgroundColor:
                  showAnswers && isCorrect ? "success.50" : "grey.50",
                border: "1px solid",
                borderColor:
                  showAnswers && isCorrect ? "success.main" : "grey.200",
                transition: "all 0.2s ease",
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor:
                    showAnswers && isCorrect ? "success.main" : "grey.200",
                  color: showAnswers && isCorrect ? "white" : "grey.400",
                }}
              >
                {showAnswers && isCorrect ? (
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                ) : null}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: showAnswers && isCorrect ? 500 : 400,
                  color:
                    showAnswers && isCorrect ? "success.dark" : "text.primary",
                }}
              >
                {option}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Render Type 4: Sentence ordering - Redesigned
  const renderSentenceOrdering = (question: PreviewQuestion) => {
    if (!question.words) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1.5, display: "block" }}
        >
          Arrange these words in the correct order:
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {question.words.map((word: string, idx: number) => (
            <Chip
              key={idx}
              label={word}
              variant="outlined"
              sx={{
                borderRadius: 2,
                backgroundColor: "grey.50",
                borderColor: "grey.300",
                fontWeight: 500,
              }}
            />
          ))}
        </Box>
        {showAnswers && question.correctSentence && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: 2,
              backgroundColor: "success.50",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "success.200",
            }}
          >
            <CheckCircleIcon sx={{ color: "success.main", mt: 0.25 }} />
            <Box>
              <Typography
                variant="caption"
                color="success.dark"
                fontWeight={600}
              >
                Correct Answer
              </Typography>
              <Typography variant="body2" color="success.dark" sx={{ mt: 0.5 }}>
                {question.correctSentence}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Render Type 8: Word matching - Redesigned
  const renderWordMatching = (question: PreviewQuestion) => {
    if (!question.wordPairs || question.wordPairs.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1.5, display: "block" }}
        >
          Match the words with their meanings:
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {question.wordPairs.map((pair: any, idx: number) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 1.5,
                backgroundColor: showAnswers ? "success.50" : "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: showAnswers ? "success.200" : "grey.200",
                transition: "all 0.2s ease",
              }}
            >
              <Chip
                label={pair.word}
                size="small"
                sx={{
                  fontWeight: 600,
                  backgroundColor: showAnswers
                    ? "success.main"
                    : "primary.main",
                  color: "white",
                  minWidth: 100,
                  "& .MuiChip-label": { px: 1.5 },
                }}
              />
              <Box
                sx={{
                  width: 24,
                  display: "flex",
                  justifyContent: "center",
                  color: showAnswers ? "success.main" : "grey.400",
                }}
              >
                →
              </Box>
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  color: showAnswers ? "success.dark" : "text.primary",
                  fontWeight: showAnswers ? 500 : 400,
                }}
              >
                {pair.meaning}
              </Typography>
              {showAnswers && (
                <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Render Type 12: Flip card content - Redesigned
  const renderFlipCard = (question: PreviewQuestion) => {
    if (!question.content) return null;

    const { questionTitle, answerTitle, questionContent, answerContent } =
      question.content;

    return (
      <Box sx={{ mt: 2 }}>
        {/* Front of card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2,
            backgroundColor: "primary.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "primary.200",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <MenuBookIcon sx={{ color: "primary.main", fontSize: 20 }} />
            <Typography
              variant="subtitle2"
              color="primary.main"
              fontWeight={600}
            >
              {questionTitle || "Front"}
            </Typography>
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          {questionContent?.map((item: any, idx: number) => (
            <Typography
              key={idx}
              variant="body2"
              sx={{
                fontWeight: item.isBold ? 600 : 400,
                mb: 0.75,
                color: "text.primary",
                lineHeight: 1.6,
              }}
            >
              {item.text}
            </Typography>
          ))}
        </Paper>

        {/* Back of card (answer) */}
        {showAnswers && answerContent && answerContent.length > 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              backgroundColor: "success.50",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "success.200",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                color="success.dark"
                fontWeight={600}
              >
                {answerTitle || "Back (Answer)"}
              </Typography>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            {answerContent.map((item: any, idx: number) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{
                  fontWeight: item.isBold ? 600 : 400,
                  mb: 0.75,
                  color: "success.dark",
                  lineHeight: 1.6,
                }}
              >
                {item.text}
              </Typography>
            ))}
          </Paper>
        ) : !showAnswers && answerContent && answerContent.length > 0 ? (
          <Box
            sx={{
              p: 2,
              backgroundColor: "grey.100",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "grey.300",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Toggle "Show Answers" to reveal the back of this card
            </Typography>
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderQuestion = (question: PreviewQuestion, index: number) => {
    const qType = question.questionType?.toString() || "unknown";

    // Get a nice label for question type
    const typeLabels: Record<string, string> = {
      "1": "Multiple Choice",
      "2": "Single Choice",
      "3": "Multi-Select",
      "4": "Ordering",
      "6": "Fill-in-Blank",
      "8": "Matching",
      "9": "Drag & Drop",
      "10": "Short Answer",
      "11": "Speaking",
      "12": "Study Card",
      "13": "Highlighting",
    };

    return (
      <Paper
        key={question.questionId}
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "grey.200",
          overflow: "hidden",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "grey.300",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          },
        }}
      >
        {/* Question Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            backgroundColor: "grey.50",
            borderBottom: "1px solid",
            borderColor: "grey.200",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Question {index + 1}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label={`${question.points} pts`}
              size="small"
              sx={{
                height: 24,
                backgroundColor: "white",
                border: "1px solid",
                borderColor: "grey.300",
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            />
            <Chip
              label={typeLabels[qType] || `Type ${qType}`}
              size="small"
              sx={{
                height: 24,
                backgroundColor: "primary.50",
                color: "primary.main",
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            />
          </Box>
        </Box>

        {/* Question Content */}
        <Box sx={{ p: 2.5 }}>
          {/* Question Text */}
          <Typography
            variant="body1"
            fontWeight={500}
            sx={{ mb: 2, lineHeight: 1.6 }}
          >
            {question.questionText}
          </Typography>

          {/* Question Image/Asset */}
          {question.asset && (
            <Box sx={{ mb: 2 }}>
              {isAudioAsset(question.asset) ? (
                <Box
                  sx={{
                    p: 1.5,
                    backgroundColor: "grey.50",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: "primary.100",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      🎧
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Audio
                    </Typography>
                  </Box>
                  <audio controls style={{ width: "100%", height: 36 }}>
                    <source src={question.asset} />
                  </audio>
                </Box>
              ) : (
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "grey.200",
                    backgroundColor: "grey.50",
                  }}
                >
                  {/* Image container with click to enlarge */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 200,
                      cursor: "zoom-in",
                      "&:hover": {
                        backgroundColor: "grey.100",
                      },
                    }}
                    onClick={() => setFullscreenImage(question.asset || null)}
                  >
                    <img
                      src={question.asset}
                      alt="Question asset"
                      style={{
                        display: "block",
                        maxWidth: "100%",
                        maxHeight: 300,
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                      }}
                    />
                  </Box>
                  {/* Zoom hint */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                      py: 1,
                      borderTop: "1px solid",
                      borderColor: "grey.200",
                      backgroundColor: "white",
                    }}
                  >
                    <FullscreenIcon sx={{ fontSize: 16, color: "grey.500" }} />
                    <Typography variant="caption" color="text.secondary">
                      Click image to enlarge
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Separate audio field */}
          {question.audio && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor: "primary.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  🎧
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Listen to Audio
                </Typography>
              </Box>
              <audio controls style={{ width: "100%", height: 36 }}>
                <source src={question.audio} />
              </audio>
            </Box>
          )}

          {/* Render based on question type */}
          {qType === "2" && question.options && renderQuestionOptions(question)}
          {qType === "3" && question.options && renderCheckboxOptions(question)}
          {(qType === "1" || qType === "6" || qType === "10") &&
            renderSubQuestions(question)}
          {qType === "4" && renderSentenceOrdering(question)}
          {qType === "8" && renderWordMatching(question)}
          {qType === "9" && question.wordPairs && renderWordMatching(question)}
          {qType === "12" && renderFlipCard(question)}

          {/* Type 9: correctMatches fallback (when no wordPairs) */}
          {qType === "9" &&
            showAnswers &&
            !question.wordPairs?.length &&
            question.correctMatches?.length && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Correct matches:
                </Typography>
                {question.correctMatches.map((match: any, idx: number) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      mb: 0.5,
                      backgroundColor: "success.50",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "success.200",
                    }}
                  >
                    <CheckCircleIcon
                      sx={{ fontSize: 16, color: "success.main" }}
                    />
                    <Typography variant="body2" color="success.dark">
                      {match.left || match.item} →{" "}
                      {match.right || match.category}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

          {/* Type 11: Speaking practice */}
          {qType === "11" && (
            <Box sx={{ mt: 2 }}>
              {question.sentences && question.sentences.length > 0 && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "grey.50",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1.5, display: "block" }}
                  >
                    Practice these sentences:
                  </Typography>
                  {question.sentences.map((sentence: string, idx: number) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{ mb: 0.5, pl: 2 }}
                    >
                      {idx + 1}. {sentence}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Type 13: Highlighting */}
          {qType === "13" && question.text && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "grey.50",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  {question.text}
                </Typography>
              </Box>
              {showAnswers && question.correctHighlights && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    backgroundColor: "success.50",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "success.200",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon
                      sx={{ fontSize: 18, color: "success.main" }}
                    />
                    <Typography variant="body2" color="success.dark">
                      Highlight: {question.correctHighlights.join(", ")}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Explanation */}
          {showAnswers && question.explanation && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                backgroundColor: "info.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "info.200",
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <LightbulbIcon
                sx={{ fontSize: 18, color: "info.main", mt: 0.25 }}
              />
              <Typography variant="body2" color="info.dark">
                {question.explanation}
              </Typography>
            </Box>
          )}

          {/* Hint */}
          {question.hint && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                backgroundColor: "warning.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "warning.200",
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Typography variant="body2" color="warning.dark">
                💡 Hint: {question.hint}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    );
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          component={RouterLink}
          to="/teacher/preview"
          underline="hover"
          color="inherit"
        >
          Content Preview
        </Link>
        <Link
          component={RouterLink}
          to={`/teacher/preview/${program}`}
          underline="hover"
          color="inherit"
        >
          {getProgramDisplayName(program || "")}
        </Link>
        <Link
          component={RouterLink}
          to={`/teacher/preview/${program}/week/${weekNumber}`}
          underline="hover"
          color="inherit"
        >
          Week {weekNumber}
        </Link>
        <Typography color="text.primary" fontWeight="medium">
          {moduleType?.charAt(0).toUpperCase() + (moduleType?.slice(1) || "")}
        </Typography>
      </Breadcrumbs>

      {/* Preview Mode Banner */}
      <PreviewBanner elevation={0}>
        <Box display="flex" alignItems="center" gap={2}>
          <VisibilityIcon color="info" />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Preview Mode
            </Typography>
            <Typography variant="body2">
              Viewing as teacher — progress is not tracked
            </Typography>
          </Box>
        </Box>
      </PreviewBanner>

      {/* Module Header */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {moduleData?.moduleName ||
            (moduleType
              ? moduleType.charAt(0).toUpperCase() + moduleType.slice(1)
              : "Module")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {moduleData?.description ||
            `${moduleType} module for Week ${weekNumber}`}
        </Typography>
        <Box display="flex" gap={2} mt={2}>
          <Chip
            label={
              moduleData?.programName || getProgramDisplayName(program || "")
            }
            variant="outlined"
          />
          <Chip label={`Week ${weekNumber}`} variant="outlined" />
          <Chip
            label={`${moduleData?.estimatedTime || 30} min`}
            variant="outlined"
          />
          {moduleData?.quiz?.questionCount &&
            moduleData.quiz.questionCount > 0 && (
              <Chip
                icon={<QuizIcon />}
                label={`${moduleData.quiz.questionCount} questions`}
                variant="outlined"
                color="primary"
              />
            )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            icon={<MenuBookIcon />}
            iconPosition="start"
            label={
              learningLoading
                ? "Loading..."
                : `Learning Content (${
                    moduleConfig?.sections?.length || 0
                  } sections)`
            }
          />
          <Tab
            icon={<QuizIcon />}
            iconPosition="start"
            label={
              quizLoading
                ? "Loading..."
                : `Quiz Preview (${
                    moduleData?.quiz?.questionCount || 0
                  } questions)`
            }
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <CustomTabPanel value={activeTab} index={0}>
        {learningLoading ? (
          <ContentContainer>
            <SectionsSidebar>
              <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={40} />
            </SectionsSidebar>
            <ContentMain>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <CircularProgress />
              </Box>
            </ContentMain>
          </ContentContainer>
        ) : loadedSections.length > 0 ? (
          <ContentContainer>
            {/* Sections Sidebar */}
            <SectionsSidebar>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontSize: "16px", fontWeight: 600 }}
              >
                Module Contents
              </Typography>

              {loadedSections.map((section, index) => {
                const isActive = activeSectionIndex === index;

                return (
                  <SectionNavItem
                    key={section.id}
                    isActive={isActive}
                    onClick={() => setActiveSectionIndex(index)}
                  >
                    <SectionNumber isActive={isActive}>
                      {index + 1}
                    </SectionNumber>
                    <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                      {section.config?.title || section.id}
                    </Typography>
                  </SectionNavItem>
                );
              })}

              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#6C757D", fontSize: "12px", mb: 1 }}
                >
                  {loadedSections.length} sections available
                </Typography>
              </Box>
            </SectionsSidebar>

            {/* Content Area */}
            <ContentMain>
              <ContentArea>
                {activeSectionIndex !== null &&
                loadedSections[activeSectionIndex] ? (
                  <Suspense
                    fallback={
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                      >
                        <CircularProgress />
                      </Box>
                    }
                  >
                    {(() => {
                      const section = loadedSections[activeSectionIndex];
                      const SectionComponent = section.component;
                      return (
                        <SectionComponent
                          onComplete={handleSectionComplete}
                          isLocked={false}
                          sectionId={`preview-${section.id}`}
                          isLastSection={
                            activeSectionIndex === loadedSections.length - 1
                          }
                        />
                      );
                    })()}
                  </Suspense>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                    color="text.secondary"
                  >
                    <MenuBookIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                    <Typography variant="h6">
                      Select a section to preview
                    </Typography>
                    <Typography variant="body2">
                      Click on any section in the sidebar to view its content
                    </Typography>
                  </Box>
                )}
              </ContentArea>
            </ContentMain>
          </ContentContainer>
        ) : !learningLoaded ? (
          <Alert severity="info">Loading learning content...</Alert>
        ) : (
          <Alert severity="info">
            No learning content available for this module.
          </Alert>
        )}
      </CustomTabPanel>

      <CustomTabPanel value={activeTab} index={1}>
        {quizLoading ? (
          <Box>
            <Skeleton
              variant="rectangular"
              height={60}
              sx={{ mb: 2, borderRadius: 2 }}
            />
            <Skeleton
              variant="rectangular"
              height={120}
              sx={{ mb: 2, borderRadius: 2 }}
            />
            <Skeleton
              variant="rectangular"
              height={120}
              sx={{ mb: 2, borderRadius: 2 }}
            />
          </Box>
        ) : moduleData?.quiz.questions &&
          moduleData.quiz.questions.length > 0 ? (
          <Box>
            {/* Quiz Header Card */}
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
                overflow: "hidden",
              }}
            >
              {/* Stats Row */}
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "grey.50",
                  borderBottom: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <QuizIcon sx={{ color: "primary.main", fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      <strong>{moduleData.quiz.questionCount}</strong> questions
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 1,
                      height: 20,
                      backgroundColor: "grey.300",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    <strong>{moduleData.quiz.totalPoints}</strong> points total
                  </Typography>
                </Box>
              </Box>

              {/* Show Answers Toggle */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "white",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      backgroundColor: showAnswers ? "success.50" : "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <VisibilityIcon
                      sx={{
                        fontSize: 18,
                        color: showAnswers ? "success.main" : "grey.500",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Answer Key
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {showAnswers
                        ? "Correct answers are highlighted"
                        : "Toggle to reveal answers"}
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={showAnswers}
                  onChange={(e) => setShowAnswers(e.target.checked)}
                  color="success"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "success.main",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "success.main",
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Questions */}
            {moduleData.quiz.questions.map((question, index) =>
              renderQuestion(question, index),
            )}
          </Box>
        ) : !quizLoaded ? (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              backgroundColor: "grey.50",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "grey.300",
            }}
          >
            <QuizIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Click to load quiz questions
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              backgroundColor: "grey.50",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "grey.300",
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No quiz questions available for this module yet
            </Typography>
          </Box>
        )}
      </CustomTabPanel>

      {/* Fullscreen Image Dialog */}
      <Dialog
        open={!!fullscreenImage}
        onClose={() => {
          setFullscreenImage(null);
          setZoomLevel(1);
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: "95vw",
            maxHeight: "95vh",
            m: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
          }}
        >
          <Typography variant="subtitle1" fontWeight={500}>
            Reference Image
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => setZoomLevel((z) => Math.max(0.5, z - 0.25))}
              disabled={zoomLevel <= 0.5}
              size="small"
            >
              <ZoomOutIcon />
            </IconButton>
            <Chip
              label={`${Math.round(zoomLevel * 100)}%`}
              size="small"
              sx={{ minWidth: 60 }}
            />
            <IconButton
              onClick={() => setZoomLevel((z) => Math.min(3, z + 0.25))}
              disabled={zoomLevel >= 3}
              size="small"
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              onClick={() => setZoomLevel(1)}
              size="small"
              title="Reset zoom"
            >
              <FullscreenIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                setFullscreenImage(null);
                setZoomLevel(1);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            minHeight: "60vh",
            overflow: "auto",
            backgroundColor: "grey.100",
          }}
        >
          {fullscreenImage && (
            <img
              src={fullscreenImage}
              alt="Reference Full"
              style={{
                maxWidth: zoomLevel === 1 ? "100%" : "none",
                maxHeight: zoomLevel === 1 ? "80vh" : "none",
                width: zoomLevel !== 1 ? `${zoomLevel * 100}%` : "auto",
                objectFit: "contain",
                transition: "all 0.2s ease",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ModulePreview;
